import * as React from "react";
import { Grid, Table, Button, Divider } from "semantic-ui-react";

import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { AnsweredQuestion, AnsweredQuestionWithId } from "@teamprevent/common/model";
import { EvidenceType, EvidenceTypes, NextStep, NextSteps } from "@teamprevent/common/immunisation/models";
import { FilesQuestion } from "@teamprevent/common/questionnaires/components/questions/FilesQuestion";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { QuestionAnswer } from "@teamprevent/common/questionnaires/model";
import { getIdentityValidationOptionsValue } from "@teamprevent/common/immunisation/models";

import { ImmunisationCreateModel } from "../../immunisations/model";

import { CreateImmunisationModal } from "./CreateImmunisationModal";

interface Props {
    index?: number;
    readonly: boolean;
    ohCaseId: string;
    questionnaireKey: string;
    questions: AnsweredQuestionWithId[];
    onTaskChange?: (taskTypeId: string, immunisationTypeId: string) => void;
    updateAnswer: (id: string, question: AnsweredQuestion, questionnaireKey: string, sectionId: string) => void;
    immunisationRecords: { immunisationModel: ImmunisationCreateModel; immunisationTypeId: string; rowId: number }[];
    employeeRecordId: string;
    onImmunisationsAdd: (immunisation: ImmunisationCreateModel, immunisationTypeId: string) => void;
    onImmunisationsRemoveForImmunisationTypeId: (immunisationTypeId: string) => void;
    onImmunisationsRemoveForRowId: (rowId: number) => void;
}

export const CovidVaccinationSection: React.FunctionComponent<Props> = (props) => {
    const sectionId = "CovidVaccinationSection";
    const newEvidenceQuestionId = "NewEvidence_CovidVaccinationSection";
    const typeOfEvidenceQuestionId = "TypeOfEvidence_CovidVaccinationSection";
    const canUploadEvidenceQuestionId = "CanUploadEvidence_CovidVaccinationSection";
    const immunisationTypeEvidenceQuestionId = "ImmunisationTypeEvidence_CovidVaccinationSection";
    const nextStepQuestionId = "NextStep_CovidVaccinationSection";
    const covidVaccinationEvidenceRequiredConsentQuestionId = "CovidVaccinationEvidenceRequiredConsent_CovidVaccinationSection";
    const covidVaccinationEvidenceSharingConsentQuestionId = "CovidVaccinationEvidenceSharingConsentQuestionId_CovidVaccinationSection";
    const [showImmunisationRecords, setImmunisationRecords] = React.useState(false);
    const [showCreateImmunisationModal, setShowCreateImmunisationModal] = React.useState(false);
    const [currentImmunisations, setCurrentImmunisations] = React.useState<{
        immunisationModel: ImmunisationCreateModel; immunisationTypeId: string; rowId: number;
    }[]>([]);

    React.useEffect(() => {
        setCurrentImmunisations(
            props.immunisationRecords
                .filter(i => i.immunisationTypeId === sectionId)
                .map(i => { return { immunisationModel: i.immunisationModel, immunisationTypeId: i.immunisationTypeId, rowId: i.rowId }; }));
    }, [props.immunisationRecords]);

    const getAnswerToQuestion = (id: string) => {
        return getAnswerToQuestionWithId(props.questions, id);
    };

    const addToImmsRecord = () => {
        setShowCreateImmunisationModal(true);
    };

    const onImmunisationsModalClose = () => {
        setShowCreateImmunisationModal(false);
    };

    const onImmunisationsAdd = (immunisation: ImmunisationCreateModel) => {
        props.onImmunisationsAdd(immunisation, sectionId);
    };

    const updateCanUploadEvidence = (value: QuestionAnswer) => {
        if (value === MultiButtonQuestion.NoOption) {
            props.updateAnswer(
                nextStepQuestionId,
                {
                    question: "What is the next step?",
                    sectionId,
                    id: nextStepQuestionId,
                    answer: NextStep.chaseEmployee,
                    isValid: true
                },
                props.questionnaireKey,
                sectionId);

            props.onTaskChange(
                NextSteps.find(t => t.value === NextStep.chaseEmployee)?.taskTypeId,
                sectionId);
        }
    };

    React.useEffect(() => {
        const typeOfEvidence = getAnswerToQuestion(typeOfEvidenceQuestionId);

        if (typeOfEvidence) {
            updateTypeOfEvidence(typeOfEvidence);
        }
    }, []);

    const updateTypeOfEvidence = (value: QuestionAnswer) => {
        const shouldShowImmunisationRecords = !props.readonly &&
            (value === EvidenceType.bcgScar ||
                value === EvidenceType.historyOfDisease ||
                value === EvidenceType.historyOfvaccination ||
                value === EvidenceType.historyOfPreviousVaricellaInfectionInTheUK);

        setImmunisationRecords(shouldShowImmunisationRecords);

        if (!shouldShowImmunisationRecords) {
            props.onImmunisationsRemoveForImmunisationTypeId(sectionId);
        }
    };

    const removeImmsRecordForRowId = (rowId: number) => {
        props.onImmunisationsRemoveForRowId(rowId);
    };

    return (
        <>
            <AccordionQuestionnaireSection
                key={sectionId}
                id={sectionId}
                title="COVID-19 Vaccination"
                index={props.index}
            >
                <MultiButtonQuestion
                    readonly={props.readonly}
                    mandatory
                    questionConfig={({
                        id: newEvidenceQuestionId,
                        questionText: "Have you seen sufficient new evidence today?"
                    })}
                />
                {getAnswerToQuestion(newEvidenceQuestionId) === MultiButtonQuestion.YesOption &&
                    <>
                        <DropdownQuestion
                            readonly={props.readonly}
                            questionConfig={({
                                id: typeOfEvidenceQuestionId,
                                questionText: "What type of evidence?"
                            })}
                            options={EvidenceTypes}
                            mandatory
                            onAnswerChange={updateTypeOfEvidence}
                        />
                        {getAnswerToQuestion(typeOfEvidenceQuestionId) === EvidenceType.documentaryEvidenceViewed &&
                            <MultiButtonQuestion
                                readonly={props.readonly}
                                mandatory
                                questionConfig={({
                                    id: canUploadEvidenceQuestionId,
                                    questionText: "Can you upload the evidence to record now?"
                                })}
                                onAnswerChange={updateCanUploadEvidence}
                            />
                        }
                        {getAnswerToQuestion(typeOfEvidenceQuestionId) === EvidenceType.documentaryEvidenceViewed &&
                            getAnswerToQuestion(canUploadEvidenceQuestionId) === MultiButtonQuestion.YesOption &&
                            <FilesQuestion
                                readonly={props.readonly}
                                questionConfig={({
                                    id: immunisationTypeEvidenceQuestionId,
                                    questionText: "Evidence"
                                })}
                                api={`/api/immunisationReview/${props.ohCaseId}/evidence`}
                                mandatory
                                acceptExtensions={acceptableExtensions}
                            />
                        }
                        {showImmunisationRecords &&
                    <>
                        <Button type="button" content="Add to Imms Record" onClick={addToImmsRecord} />
                        <br />
                        {currentImmunisations?.length > 0 &&
                        <>
                            <br />
                            <Grid container>
                                <Grid.Row >
                                    <Grid.Column width={16}>
                                        <h1>Immunisations</h1>
                                    </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                    <Grid.Column width={16}>
                                        <Table>
                                            <Table.Header>
                                                <Table.Row>
                                                    <Table.HeaderCell>Immunisation</Table.HeaderCell>
                                                    <Table.HeaderCell>Date complete</Table.HeaderCell>
                                                    <Table.HeaderCell>Type</Table.HeaderCell>
                                                    <Table.HeaderCell>Outcome</Table.HeaderCell>
                                                    <Table.HeaderCell>IVS Sample</Table.HeaderCell>
                                                    <Table.HeaderCell>Completed by</Table.HeaderCell>
                                                    <Table.HeaderCell>Remove</Table.HeaderCell>
                                                </Table.Row>
                                            </Table.Header>
                                            <Table.Body>
                                                {currentImmunisations.map(immunisation => (
                                                    <Table.Row key={immunisation.rowId}>
                                                        <Table.Cell>
                                                            {immunisation.immunisationModel.immunisationTypeName}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {immunisation.immunisationModel.dateCompleted &&
                                                                immunisation.immunisationModel.dateCompleted.format("DD/MM/YYYY")}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {immunisation.immunisationModel.recordType}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {immunisation.immunisationModel.outcome}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {getIdentityValidationOptionsValue(immunisation.immunisationModel.identityValidation)}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {immunisation.immunisationModel.completedByName}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <Button
                                                                type="button"
                                                                content="Remove"
                                                                onClick={() => removeImmsRecordForRowId(immunisation.rowId)} />
                                                        </Table.Cell>
                                                    </Table.Row>
                                                ))}
                                            </Table.Body>
                                        </Table>
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                            <br />
                        </>
                        }
                        <Divider />
                    </>
                        }
                    </>
                }
                {(getAnswerToQuestion(newEvidenceQuestionId) === MultiButtonQuestion.NoOption ||
                    getAnswerToQuestion(canUploadEvidenceQuestionId) === MultiButtonQuestion.NoOption) &&
                    <DropdownQuestion
                        readonly={props.readonly}
                        questionConfig={({
                            id: nextStepQuestionId,
                            questionText: "What is the next step?"
                        })}
                        options={NextSteps.map(n => ({ value: n.value, text: n.text }))}
                        mandatory
                        onAnswerChange={(value) => props.onTaskChange(
                            NextSteps.find(t => t.value === value?.toString())?.taskTypeId,
                            sectionId)}
                    />
                }
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={({
                        id: covidVaccinationEvidenceRequiredConsentQuestionId,
                        questionText: "I can confirm that the employee understands that evidence of COVID-19 vaccination status is required for this role"
                    })}
                    value="Confirmed"
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={({
                        id: covidVaccinationEvidenceSharingConsentQuestionId,
                        questionText: `I can confirm that the employee has given consent for Occupational Health to share their
COVID-19 vaccination history including dates of vaccination with their employer`
                    })}
                    value="Confirmed"
                />
            </AccordionQuestionnaireSection>
            {showCreateImmunisationModal &&
                <CreateImmunisationModal
                    employeeRecordId={props.employeeRecordId}
                    onClose={onImmunisationsModalClose}
                    onSave={onImmunisationsAdd}
                />
            }
        </>
    );
};
